import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { label, tooltip } from "../../../../../config/constants";
import { pascalToCamel } from "../../../../../helper/common";
import { getUserEmailControl, handleUserEmailControl } from "../../../../../shared/services/user.service";
import { emailNotificationOptions, emailNotificationSettingsArray } from "./userProfileSidebar.constants";

/**
 * EmailNotificationsSection component for Email Notifications section
 * Displays Email Notifications parent toggle and child toggles (Assigned Tasks, Followed Tasks, and Daily Digest Updates)
 * Uses reusable optionsData from constants
 * @author Bhavana
 * @param {string} [emailControlUserId] — when set (e.g. admin managing another user), load/save for this id; otherwise same as before.
 */
const EmailNotificationsSection = ({ emailControlUserId, isAdmin } = {}) => {
  const [emailSettings, setEmailSettings] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [isTaskUpdateEnabled, setIsTaskUpdateEnabled] = useState(false);
  const [isFollowerUpdateEnabled, setIsFollowerUpdateEnabled] = useState(false);
  const [isDailyDigestEnabled, setIsDailyDigestEnabled] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { uId } = useParams();
  const { visitingByAdmin, details } = useSelector((state) => state.userDetails);

  const emailUserId = emailControlUserId ?? (visitingByAdmin ? details.id : user.id);

  /**
   * Fetches user email control settings. Response: { taskEmails: [{ ... }], workDigest: [{ ... }] }
   */
  useEffect(() => {
    (async () => {
      const data = await getUserEmailControl(emailUserId , isAdmin);
      setEmailSettings(data ?? null);
      if (data?.taskEmails?.[0]) {
        const te = data.taskEmails[0];
        setIsTaskUpdateEnabled(!!te.IsTaskUpdateEnabled);
      }
      if (data?.workDigest?.[0]) {
        setIsDailyDigestEnabled(!!data.workDigest[0].IsDailyDigestEnabled);
      }
    })();
  }, [emailUserId]);

  /** Section record from API (taskEmails[0] or workDigest[0]) */
  const getSectionRecord = (section) => {
    const sectionDataArray = emailSettings?.[section.sectionDataSource];
    return Array.isArray(sectionDataArray) && sectionDataArray.length ? sectionDataArray[0] : null;
  };

  /** Section-level switch: use API value or fallback to constant default */
  const getSectionEnabled = (section) => {
    const sectionRecord = getSectionRecord(section);
    const apiKey = section.sectionEnabledApiKey;
    if (sectionRecord && apiKey != null) return !!sectionRecord[apiKey];
    return !!section.enabled;
  };

  /** Notification-level switch: use API value or fallback to constant default */
  const getNotificationEnabled = (section, notification) => {
    const sectionRecord = getSectionRecord(section);
    if (sectionRecord && notification.apiKey != null) return !!sectionRecord[notification.apiKey];
    return !!notification.enabled;
  };

  /** Frequency dropdown value from API (EmailTriggerDuration: 12, 24, 3, 6) or constant default. Number type so DropDownList matches option and shows label. */
  const getFrequencyValue = (section) => {
    const sectionRecord = getSectionRecord(section);
    const apiKey = section.frequency?.apiKey;
    if (sectionRecord && apiKey != null && sectionRecord[apiKey] != null) return Number(sectionRecord[apiKey]);
    return section.frequency?.value ?? 12;
  };

  /**
   * Updates user preferences based on email notification states
   * @author Bhavana
   */
  useEffect(() => {
    const isEnabled = [isTaskUpdateEnabled, isDailyDigestEnabled, isFollowerUpdateEnabled].some((enabled) => enabled);
    setShowNotification(isEnabled);
  }, [isTaskUpdateEnabled, isDailyDigestEnabled, isFollowerUpdateEnabled]);

  /** Merge payload (camelCase keys) into emailSettings for optimistic UI. Maps payload keys to [sectionDataSource, API key]. */
  const payloadToSectionAndApi = {
    isTaskUpdateEnabled: ["taskEmails", "IsTaskUpdateEnabled"],
    isApprovalEmailEnabled: ["taskEmails", "IsApprovalEmailEnabled"],
    isTaskAssignEmailEnabled: ["taskEmails", "IsTaskAssignEmailEnabled"],
    isCommentEmailEnabled: ["taskEmails", "IsCommentEmailEnabled"],
    isDailyDigestEnabled: ["workDigest", "IsDailyDigestEnabled"],
    emailTriggerDuration: ["workDigest", "EmailTriggerDuration"],
    approvalsPanel: ["workDigest", "ApprovalsPanel"],
    overdueAssignmentPanel: ["workDigest", "OverdueAssignmentPanel"],
    commentsPanel: ["workDigest", "CommentsPanel"],
    remindersPanel: ["workDigest", "RemindersPanel"],
    unseenEmailPanel: ["workDigest", "UnseenEmailPanel"],
    streamsPanel: ["workDigest", "StreamsPanel"],
    followedTaskPanel: ["workDigest", "FollowedTaskPanel"],
  };

  const mergePayloadIntoEmailSettings = (payload) => {
    if (!emailSettings || typeof payload !== "object") return;
    setEmailSettings((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      Object.keys(payload).forEach((key) => {
        const [dataSource, apiKey] = payloadToSectionAndApi[key] || [];
        if (dataSource && apiKey && next[dataSource]?.[0]) {
          next[dataSource][0][apiKey] = payload[key];
        }
      });
      return next;
    });
  };

  /**
   * Single handler for all switch changes. Sends only the updated key(s) to changeUserEmailControl.
   * When section header is disabled, only the section toggle is sent in payload (child notifications retain their values).
   */
  const handleSwitchChange = (emailSection, key, value) => {
    let payload = {};
    if (key === emailSection.section) {
      const sectionPayloadKey = pascalToCamel(emailSection.sectionEnabledApiKey);
      payload[sectionPayloadKey] = value;
    } else {
      const notification = emailSection.notifications?.find((n) => n.key === key);
      if (notification?.apiKey) {
        payload[pascalToCamel(notification.apiKey)] = value;
      }
    }
    if (Object.keys(payload).length === 0) return;
    dispatch(
      handleUserEmailControl(
        emailControlUserId != null ? { ...payload, selectedUserId: emailControlUserId } : payload,
        isAdmin
      )
    );
    mergePayloadIntoEmailSettings(payload);
  };

  /** Frequency dropdown change: send only emailTriggerDuration (number). */
  const handleFrequencyChange = (section, value) => {
    const payload = { emailTriggerDuration: value };
    dispatch(
      handleUserEmailControl(
        emailControlUserId != null ? { ...payload, selectedUserId: emailControlUserId } : payload,
        isAdmin
      )
    );
    mergePayloadIntoEmailSettings(payload);
  };

  // Show all email notification options including Daily Digest Updates
  const visibleOptions = emailNotificationOptions;

  /**
   * Renders section header with label and description on separate lines
   * @param {string} label - Section or notification label
   * @param {string} description - Section or notification description
   * @returns {JSX.Element} Header element with label and description
   * @author Abhishek Dhillon
   */
  const renderSectionHeader = (label, description) => {
    return (
      <div>
        <div className='font-weight-medium'>{label}</div>
        <div className='text-muted small'>{description}</div>
      </div>
    );
  };

  return (
    <div className='profile-section-content bg-light mt-1'>
      {emailNotificationSettingsArray.map((section) => {
        return (
          <div key={section.section}>
            <div className='d-flex justify-content-between align-items-center login-setting-container mb-2'>
              {renderSectionHeader(section.sectionLabel, section.sectionDescription)}
                <button
                  type='button'
                  className={`login-toggle-btn ${getSectionEnabled(section) ? "login-toggle-btn-disable" : "login-toggle-btn-disabled"}`}
                  title={getSectionEnabled(section) ? tooltip.CLICK_TO_DISABLE : tooltip.CLICK_TO_ENABLE}
                  onClick={() => handleSwitchChange(section, section.section, !getSectionEnabled(section))}>
                  {getSectionEnabled(section) ? label.DISABLE : label.DISABLED}
                </button>
            </div>
            {getSectionEnabled(section) && section?.frequency && (
              <div className='d-flex justify-content-between align-items-center py-2'>
                {renderSectionHeader(section?.frequency?.label, section?.frequency?.description)}
                <DropDownList
                  data={section?.frequency?.options}
                  value={section?.frequency?.options?.find((opt) => opt.value === getFrequencyValue(section)) ?? null}
                  dataItemKey='value'
                  className={emailControlUserId ? 'w-40' : 'w-25'}
                  textField='label'
                  valueField='value'
                  onChange={({ value }) => handleFrequencyChange(section, value?.value ?? value)}
                />
              </div>
            )}
            {getSectionEnabled(section) && section.notifications.map((notification) => {
              return (
                <div key={notification.key} className='notification-description-label border p-2 border-radius-10'>
                  <div className='d-flex justify-content-between align-items-center'>
                    {renderSectionHeader(notification.label, notification.description)}
                    <Switch
                      checked={getNotificationEnabled(section, notification)}
                      onChange={({ value }) => handleSwitchChange(section, notification.key, value)}
                    />
                  </div>
                </div>
              );
            })}
            <hr className='my-3' />
          </div>
        );
      })}
    </div>
  );
};

export default EmailNotificationsSection;
